import type { CmsWorkspaceLaunchDetail } from '@/cms/components/blocks/workspace/workspace-session-controller';

export type SiteRuntimeWorkspaceTransition =
  | { phase: 'idle' }
  | { phase: 'auth-return-failed' }
  | {
      authStatus: 'validating' | 'authenticated';
      detail: CmsWorkspaceLaunchDetail;
      intentStatus: 'validating' | 'validated';
      phase: 'preparing-auth-return';
      previousWorkspaceDetail: CmsWorkspaceLaunchDetail | null;
      shellStatus: 'loading' | 'ready';
    }
  | {
      detail: CmsWorkspaceLaunchDetail;
      phase: 'active';
      shellStatus: 'loading' | 'ready';
    };

export type SiteRuntimeWorkspaceTransitionEvent =
  | {
      detail: CmsWorkspaceLaunchDetail;
      type: 'auth-return-started';
    }
  | { type: 'auth-authenticated' }
  | { type: 'auth-intent-validated' }
  | { type: 'shell-ready' }
  | {
      detail: CmsWorkspaceLaunchDetail;
      type: 'activated';
    }
  | { type: 'auth-return-aborted' }
  | { type: 'auth-return-failed' }
  | { type: 'reset' };

export const idleSiteRuntimeWorkspaceTransition: SiteRuntimeWorkspaceTransition =
  {
    phase: 'idle',
  };

export function reduceSiteRuntimeWorkspaceTransition(
  state: SiteRuntimeWorkspaceTransition,
  event: SiteRuntimeWorkspaceTransitionEvent
): SiteRuntimeWorkspaceTransition {
  if (event.type === 'reset') {
    return idleSiteRuntimeWorkspaceTransition;
  }

  if (event.type === 'auth-return-aborted') {
    if (
      state.phase === 'preparing-auth-return' &&
      state.previousWorkspaceDetail
    ) {
      return {
        detail: state.previousWorkspaceDetail,
        phase: 'active',
        shellStatus: state.shellStatus,
      };
    }

    return state.phase === 'preparing-auth-return'
      ? idleSiteRuntimeWorkspaceTransition
      : state;
  }

  if (event.type === 'auth-return-failed') {
    if (
      state.phase === 'preparing-auth-return' &&
      state.previousWorkspaceDetail
    ) {
      return {
        detail: state.previousWorkspaceDetail,
        phase: 'active',
        shellStatus: state.shellStatus,
      };
    }

    return state.phase === 'preparing-auth-return'
      ? { phase: 'auth-return-failed' }
      : state;
  }

  if (event.type === 'activated') {
    return {
      detail: event.detail,
      phase: 'active',
      shellStatus:
        state.phase === 'active' || state.phase === 'preparing-auth-return'
          ? state.shellStatus
          : 'loading',
    };
  }

  if (event.type === 'auth-return-started') {
    return {
      authStatus: 'validating',
      detail: event.detail,
      intentStatus: 'validating',
      phase: 'preparing-auth-return',
      previousWorkspaceDetail:
        state.phase === 'active'
          ? state.detail
          : state.phase === 'preparing-auth-return'
            ? state.previousWorkspaceDetail
            : null,
      shellStatus:
        state.phase === 'active' || state.phase === 'preparing-auth-return'
          ? state.shellStatus
          : 'loading',
    };
  }

  if (
    event.type === 'shell-ready' &&
    (state.phase === 'active' || state.phase === 'preparing-auth-return')
  ) {
    return {
      ...state,
      shellStatus: 'ready',
    };
  }

  if (state.phase !== 'preparing-auth-return') {
    return state;
  }

  if (event.type === 'auth-authenticated') {
    return {
      ...state,
      authStatus: 'authenticated',
    };
  }

  if (event.type === 'auth-intent-validated') {
    return {
      ...state,
      intentStatus: 'validated',
    };
  }

  return state;
}

export function isSiteRuntimeWorkspaceTransitionReady(
  transition: SiteRuntimeWorkspaceTransition
): transition is Extract<
  SiteRuntimeWorkspaceTransition,
  { phase: 'preparing-auth-return' }
> & {
  authStatus: 'authenticated';
  intentStatus: 'validated';
  shellStatus: 'ready';
} {
  return (
    transition.phase === 'preparing-auth-return' &&
    transition.authStatus === 'authenticated' &&
    transition.intentStatus === 'validated' &&
    transition.shellStatus === 'ready'
  );
}

export function shouldMountSiteRuntimeWorkspace(
  transition: SiteRuntimeWorkspaceTransition
) {
  return (
    transition.phase === 'active' ||
    transition.phase === 'preparing-auth-return'
  );
}

export function getSiteRuntimeWorkspaceMountDetail(
  transition: SiteRuntimeWorkspaceTransition
) {
  if (transition.phase === 'active') {
    return transition.detail;
  }

  return transition.phase === 'preparing-auth-return'
    ? transition.previousWorkspaceDetail
    : null;
}
